import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
    PublicOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GET } from "../../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { UserRegionsTree } from "../../User/UserRegionsTree";

export const getAllFixedBills = (pagination: any, filter: any) => {
    return GET("/reading/billing-rule?", {
        page: pagination.page + 1,
        limit: pagination.limit,
        ...Object.fromEntries(
            Object.entries(filter).filter(([k, v]) => v && v !== "all")
        ),
    });
};

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: number | "" | "all";
    connectionType: number | "" | "all";
};

type Region = { id: number; name: string };

type Regions = {
    district: Region;
    village: Region | null;
    hamlet: Region | null;
    powerstation: Region | null;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    connectionType: "",
};

const initialClear = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    search: false,
    surcharge: false,
    lineRent: false,
    unitChargeRule: false,
    connectionType: false,
};

const FixedBill = () => {
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [load, setLoad] = useState(false);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [clearAll, setClearAll] = useState(initialClear);
    const [feedback, setFeedback] = useState("");
    const [showRegions, setShowRegions] = useState<any | null>(null);
    const [showUsers, setShowUsers] = useState<any | null>(null);
    const [showMeters, setShowMeters] = useState<any | null>(null);
    const [changeCount, setChangeCount] = useState(0);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const { data, isLoading, isError, isSuccess } = useQuery(
        ["bill-rule", pagination.page, pagination.limit, filter],
        () => getAllFixedBills(pagination, filter),
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    const getGroupedRegions = useMemo(() => {
        if (showRegions?.regions) {
            const activeUserRegions: Regions[] = showRegions.regions ?? [];

            const targetRegions = activeUserRegions.reduce(
                (result: any, item) => {
                    const { district, village, hamlet } = item;

                    // Group by district
                    const districtKey = district.name;
                    if (!result[districtKey]) {
                        result[districtKey] = {
                            villages: {},
                        };
                    }

                    // Group by village
                    if (village) {
                        const villageKey = village.name;
                        if (!result[districtKey].villages[villageKey]) {
                            result[districtKey].villages[villageKey] = {
                                hamlets: [],
                            };
                        }

                        // Group by hamlet
                        if (hamlet) {
                            result[districtKey].villages[
                                villageKey
                            ].hamlets.push(hamlet.name);
                        }
                    }

                    return result;
                },
                {}
            );

            return targetRegions;
        }
    }, [showRegions?.regions?.length]);

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            search: true,
            surcharge: true,
            lineRent: true,
            unitChargeRule: true,
            connectionType: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    hamlet: false,
                    powerstation: false,
                    search: false,
                    surcharge: false,
                    lineRent: false,
                    unitChargeRule: false,
                    connectionType: false,
                }),
            0
        );
    };

    useEffect(() => {
        if (changeCount) {
            setFilter({ ...filter, ...intermediateFilter });
        }
    }, [changeCount]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // height: mdDown ? "auto" : `calc(100vh - 96px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                >
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" color="GrayText">
                            Fixed Bill
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} container spacing={1.5}>
                        <Grid item flex={1}></Grid>

                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize
                                ops={["CREATE_BILLING_RULE"]}
                            >
                                <Link
                                    to="/fixed-bill/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        sx={{ height: "100%" }}
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        fullWidth
                                    >
                                        add fixed bill
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} container spacing={1.5}>
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/district"
                                    label="District"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/powerstation"
                                    label="Powerstation"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={intermediateFilter.district}
                                    params={{
                                        district: intermediateFilter.district,
                                    }}
                                    clearAll={clearAll.powerstation}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/village"
                                    label="Village"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        district: intermediateFilter.district,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.village}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/hamlet"
                                    label="Hamlet"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.village,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        village: intermediateFilter.village,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.hamlet}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize
                            ops={["READ CONNECTION_TYPE"]}
                        >
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/connection"
                                    label="Connection Type"
                                    _name="connectionType"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.village,
                                        intermediateFilter.hamlet,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={Object.fromEntries(
                                        Object.entries(
                                            intermediateFilter
                                        ).filter(
                                            ([k, v]) =>
                                                [
                                                    "district",
                                                    "village",
                                                    "hamlet",
                                                    "powerstation",
                                                ].includes(k) &&
                                                v &&
                                                v !== "all"
                                        )
                                    )}
                                    clearAll={clearAll.connectionType}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} md={6} lg={3}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clear={clearAll.search}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => setChangeCount(changeCount + 1)}
                                sx={{ height: "100%" }}
                                startIcon={
                                    <FilterAltOutlined fontSize="small" />
                                }
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{
                                    height: "100%",
                                    minWidth: theme.spacing(20),
                                }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    {!data?.data.rows.length && isSuccess
                        ? noDataMessage
                        : !hasValue(filter) && !changeCount
                          ? filterSelectionMessage
                          : ""}
                </Alert>
            )}

            <Box sx={{ height: "60%" }}>
                <BaseTable
                    headers={{
                        id: "ID",
                        name: "Name",
                        minimumBillAmount: "Minimum Bill Amount",
                        connectionType: "Connection Type",
                        regions: "Regions",
                        users: "Users",
                        meters: "Meters",
                        status: "status",
                    }}
                    defaultSelectedHeaders={[
                        "id",
                        "name",
                        "minimumBillAmount",
                        "connectionType",
                        "status",
                        "regions",
                        "users",
                        "meters",
                    ]}
                    _printables={["id", "name", "status", "connectionType"]}
                    data={data?.data.rows.map((row: any) => ({
                        ...row,
                        connectionType: row.connectionType.name,
                        status: (
                            <Chip
                                sx={{ minWidth: "65px" }}
                                size="small"
                                variant="outlined"
                                label={
                                    row.status === "ACTIVE"
                                        ? "Active"
                                        : "Inactive"
                                }
                                color={
                                    row.status === "ACTIVE"
                                        ? "success"
                                        : "error"
                                }
                            />
                        ),

                        regions:
                            row.BillingRuleRegions?.length == 0 ? (
                                "Global"
                            ) : (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setShowRegions({
                                            regions: row.BillingRuleRegions,
                                        })
                                    }
                                    color={
                                        row.BillingRuleRegions.length === 0
                                            ? "error"
                                            : "success"
                                    }
                                >
                                    <PublicOutlined fontSize="medium" />
                                </IconButton>
                            ),

                        users:
                            row.billingRuleUsers?.length === 0 ? (
                                "All Users"
                            ) : (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setShowUsers({
                                            name: row?.name,
                                            users: row?.billingRuleUsers?.map(
                                                (bru: any) => bru.user
                                            ),
                                        })
                                    }
                                    color="primary"
                                >
                                    <Avatar sx={{ width: 28, height: 28 }}>
                                        {row.billingRuleUsers.length}
                                    </Avatar>
                                </IconButton>
                            ),

                        meters:
                            row.billingRuleMeters?.length === 0 ? (
                                "All Meters"
                            ) : (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setShowMeters({
                                            name: row?.name,
                                            meters: row?.billingRuleMeters?.map(
                                                (brm: any) => brm.meter
                                            ),
                                        })
                                    }
                                    color="secondary"
                                >
                                    <Avatar sx={{ width: 28, height: 28 }}>
                                        {row.billingRuleMeters.length}
                                    </Avatar>
                                </IconButton>
                            ),
                    }))}
                    load={Boolean(changeCount)}
                    delEndPoint="/reading/billing-rule"
                    feedback={feedback}
                    setFeedback={setFeedback}
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    permissions={{
                        edit: ["UPDATE_BILLING_RULE"],
                        delete: ["DELETE_BILLING_RULE"],
                    }}
                    queryKey={[
                        "bill-rule",
                        pagination.page,
                        pagination.limit,
                        filter,
                    ]}
                    reportName="FixedBill__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                />

                <Dialog
                    fullWidth
                    open={Boolean(showRegions)}
                    onClose={() => setShowRegions(null)}
                >
                    <DialogTitle>Region&nbsp;access</DialogTitle>
                    <DialogContent>
                        {showRegions && showRegions.regions.length === 0 && (
                            <Typography>This fixed bill global</Typography>
                        )}
                        {Boolean(showRegions) &&
                            Boolean(showRegions.regions.length > 0) && (
                                <UserRegionsTree regions={getGroupedRegions} />
                            )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowRegions(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    fullWidth
                    open={Boolean(showUsers)}
                    onClose={() => setShowUsers(null)}
                >
                    <DialogTitle>
                        User&nbsp;access&nbsp;-&nbsp;{showUsers?.name}
                    </DialogTitle>
                    <DialogContent>
                        <List dense>
                            {showUsers && showUsers.users.length === 0 && (
                                <Typography>
                                    All users have access to this fixed bill
                                </Typography>
                            )}
                            {showUsers?.users.map((user: any) => (
                                <ListItem key={user.id}>
                                    <ListItemAvatar>
                                        <Avatar>
                                            {user.name
                                                .split(" ")
                                                .map((part: string) => part[0])
                                                .join("")
                                                .toUpperCase()}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={user.name} />
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowUsers(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    fullWidth
                    open={Boolean(showMeters)}
                    onClose={() => setShowMeters(null)}
                >
                    <DialogTitle>
                        Meter&nbsp;access&nbsp;-&nbsp;{showMeters?.name}
                    </DialogTitle>
                    <DialogContent>
                        <List dense>
                            {showMeters && showMeters.meters.length === 0 && (
                                <Typography>
                                    This fixed bill applies to all meters in scope
                                </Typography>
                            )}
                            {showMeters?.meters.map((meter: any) => (
                                <ListItem key={meter.id}>
                                    <ListItemText primary={meter.meterNo} />
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowMeters(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default FixedBill;
